/**
 * How a person is addressed at the top of their own portal.
 *
 * The rule that matters: an honorific belongs to a name, never to a placeholder.
 * Instructor and student dashboards used `honorific ? \`${honorific} ${name}\``
 * where `name` had already fallen back to the literal "there", so anyone whose
 * name had not been recorded was greeted "As-salāmu ʿalaykum, Sayyid there".
 *
 * A greeting with no name is not a broken greeting - "As-salāmu ʿalaykum" and
 * "Good morning" are both complete sentences. So the fallback is nothing at all,
 * not a stand-in word.
 */

/** The address form, or "" when no real name is known. */
export function personAddress(
  honorific: string | null | undefined,
  ...candidates: (string | null | undefined)[]
): string {
  const name = candidates.map((value) => value?.trim()).find((value) => value) ?? "";
  if (!name) return "";
  const title = honorific?.trim();
  if (!title) return name;
  const normalized = (value: string) => value.toLocaleLowerCase().replaceAll(".", "");
  const firstNamePart = name.split(/\s+/, 1)[0];
  return normalized(firstNamePart) === normalized(title) ? name : `${title} ${name}`;
}

/** `", Sayyid Fatima"` or `""`, for appending to a fixed salutation. */
export function greetingSuffix(
  honorific: string | null | undefined,
  ...candidates: (string | null | undefined)[]
): string {
  const address = personAddress(honorific, ...candidates);
  return address ? `, ${address}` : "";
}

/** The first word of a name, for the informal registrar greeting. */
export function firstName(full: string | null | undefined): string {
  return full?.trim().split(/\s+/)[0] ?? "";
}
